import React from 'react';
import {
    Target,
    MapPin,
    Calendar,
    TrendingUp,
    Settings,
    CheckCircle,
    AlertTriangle,
    Clock,
    Activity
} from 'lucide-react';

const PCECard = ({ pce, onViewConsumption }) => {
    const getStatusConfig = (status) => {
        switch (status) {
            case 'connected':
                return {
                    icon: CheckCircle,
                    text: 'Connecté',
                    color: 'text-green-600',
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200'
                };
            case 'not_connected':
                return {
                    icon: AlertTriangle,
                    text: 'Non connecté',
                    color: 'text-gray-500',
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200'
                };
            case 'token_expired':
                return {
                    icon: Clock,
                    text: 'Token expiré',
                    color: 'text-yellow-600',
                    bgColor: 'bg-yellow-50',
                    borderColor: 'border-yellow-200'
                };
            case 'error':
                return {
                    icon: AlertTriangle,
                    text: 'Erreur',
                    color: 'text-red-600',
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200'
                };
            default:
                return {
                    icon: Clock,
                    text: 'En attente',
                    color: 'text-gray-500',
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200'
                };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatLastSync = (dateString) => {
        if (!dateString) return 'Jamais synchronisé';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return 'Il y a moins d\'une heure';
        if (diffHours < 24) return `Il y a ${diffHours} heure(s)`;
        if (diffDays < 7) return `Il y a ${diffDays} jour(s)`;

        return formatDate(dateString);
    };

    const statusConfig = getStatusConfig(pce.connection_status);
    const StatusIcon = statusConfig.icon;

    const hasInstallation =
        pce.installations &&
        typeof pce.installations === 'object' &&
        !Array.isArray(pce.installations) &&
        pce.installations.name;

    return (
        <div className="bg-white rounded-lg shadow border hover:shadow-md transition-shadow duration-200">
            {/* Header */}
            <div className="p-6 pb-4">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {pce.site_name}
                        </h3>
                        <p className="text-sm text-gray-600 font-mono">
                            {pce.pce_number}
                        </p>
                    </div>
                </div>
                {/* Statut */}
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mt-3 ${statusConfig.bgColor} ${statusConfig.borderColor} ${statusConfig.color} border`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.text}
                </div>
            </div>

            {/* Infos */}
            <div className="px-6 pb-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{pce.address?.city || 'Ville non renseignée'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="h-4 w-4" />
                    <span>{pce.contract_flow} m³/h - {pce.contract_type}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>Créé le {formatDate(pce.created_at)}</span>
                </div>
                {pce.last_sync && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <TrendingUp className="h-4 w-4" />
                        <span>Sync: {formatLastSync(pce.last_sync)}</span>
                    </div>
                )}
            </div>

            {/* Installation liée */}
            {hasInstallation && (
                <div className="px-6 pb-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                            <Settings className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-900">
                                {pce.installations.name}
                            </span>
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                            Statut: {pce.installations.status}
                        </p>
                    </div>
                </div>
            )}

            {/* Bouton consultation (si consommation disponible) */}
            {pce.connection_status === 'connected' && onViewConsumption && (
                <div className="px-6 pb-6">
                    <button
                        onClick={() => onViewConsumption(pce)}
                        className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center justify-center gap-1"
                    >
                        <Activity className="h-4 w-4" />
                        Voir les données
                    </button>
                </div>
            )}
        </div>
    );
};

export default PCECard;
